import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, CornerLeftUp } from 'lucide-react';
import type { BaseplanCode, CodeBoard as CodeBoardData } from '@/types/oscar';

type SortKey = 'hierarchy' | 'code' | 'name' | 'area' | 'region';

const byText = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });

/**
 * The reporting branches as one table (Anthony, 25/10/2026: "it would be good to have
 * this as a list view as well"). Same rows, filters and editor as the board; the board
 * is for moving codes between areas, the list is for reading them across. Default
 * order is the report order: region, area, sort, code. Unallocated rows sort last.
 */
export function ReportingBranchList({
  board, codes, flags, onOpen,
}: {
  board: CodeBoardData;
  codes: BaseplanCode[];
  flags: boolean;
  onOpen: (c: BaseplanCode) => void;
}) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'hierarchy', desc: false });

  const rows = useMemo(() => {
    const area = (id: number | null) => board.Areas.find((a) => a.AreaId === id);
    const region = (id: number | null | undefined) => board.Regions.find((r) => r.RegionId === id);
    const list = codes.map((c) => {
      const a = area(c.AreaId);
      const r = region(a?.RegionId);
      return { c, areaName: a?.AreaDesc ?? null, areaSort: a?.AreaSort ?? null, regionName: r?.RegionDesc ?? null, regionSort: r?.RegionSort ?? null };
    });
    type Row = (typeof list)[number];
    // Unallocated (no area / no region) always last, whatever the direction.
    const last = (x: string | null, y: string | null) => (x === null ? 1 : 0) - (y === null ? 1 : 0);
    const cmp: Record<SortKey, (x: Row, y: Row) => number> = {
      hierarchy: (x, y) => byText(x.regionSort, y.regionSort) || byText(x.regionName, y.regionName)
        || byText(x.areaSort, y.areaSort) || byText(x.areaName, y.areaName)
        || byText(x.c.SortOrder, y.c.SortOrder) || byText(x.c.Code, y.c.Code),
      code: (x, y) => byText(x.c.Code, y.c.Code),
      name: (x, y) => byText(x.c.Description, y.c.Description) || byText(x.c.Code, y.c.Code),
      area: (x, y) => byText(x.areaName, y.areaName) || byText(x.c.Code, y.c.Code),
      region: (x, y) => byText(x.regionName, y.regionName) || byText(x.areaName, y.areaName) || byText(x.c.Code, y.c.Code),
    };
    const nulls = sort.key === 'region' ? (x: Row, y: Row) => last(x.regionName, y.regionName) : (x: Row, y: Row) => last(x.areaName, y.areaName);
    return [...list].sort((x, y) => nulls(x, y) || (sort.desc ? -1 : 1) * cmp[sort.key](x, y));
  }, [board, codes, sort]);

  const header = (key: SortKey, label: string) => {
    const active = sort.key === key;
    const Arrow = sort.desc ? ArrowDown : ArrowUp;
    return (
      <th className="px-3 py-2" aria-sort={active ? (sort.desc ? 'descending' : 'ascending') : 'none'}>
        <button
          type="button"
          onClick={() => setSort(active ? { key, desc: !sort.desc } : { key, desc: false })}
          className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-navy"
        >
          {label}
          {active && <Arrow className="h-3 w-3" aria-hidden />}
        </button>
      </th>
    );
  };

  return (
    <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
      <table className="w-full text-sm" aria-label="Reporting branches">
        <thead className="bg-[#F4F6F9] text-left text-xs text-darkGrey/70">
          <tr>
            {header('code', 'Code')}
            {header('name', 'Reporting branch')}
            {header('area', 'Area')}
            {header('region', 'Region')}
            <th className="px-3 py-2 uppercase tracking-wide">Rolls up to</th>
            <th className="px-3 py-2 uppercase tracking-wide">Type</th>
            <th className="px-3 py-2 uppercase tracking-wide">Sort</th>
            <th className="px-3 py-2 text-right uppercase tracking-wide" title="Physical locations holding the code">Locations</th>
            <th className="px-3 py-2 text-right uppercase tracking-wide" title="Active people with it as their primary branch">People</th>
            <th className="px-3 py-2 uppercase tracking-wide">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ c, areaName, regionName }) => (
            <tr key={c.Code} className={`border-t border-border ${c.IsActive ? '' : 'text-darkGrey/50'}`}>
              <td className="px-3 py-1.5 font-mono font-semibold text-navy">
                <button type="button" onClick={() => onOpen(c)} className="hover:underline" aria-label={`Open ${c.Code} ${c.Description}`}>{c.Code}</button>
              </td>
              <td className="px-3 py-1.5">{c.Description}</td>
              <td className="px-3 py-1.5">{areaName ?? <span className="italic text-darkGrey/50">Unallocated</span>}</td>
              <td className="px-3 py-1.5">{regionName ?? <span className="text-darkGrey/50">—</span>}</td>
              <td className="px-3 py-1.5">
                {c.ParentCode
                  ? <span className="inline-flex items-center gap-0.5 font-mono text-xs"><CornerLeftUp className="h-3 w-3 text-darkGrey/50" aria-hidden />{c.ParentCode}</span>
                  : <span className="text-darkGrey/40">—</span>}
              </td>
              <td className="px-3 py-1.5">
                <span className="inline-flex gap-1">
                  {c.IsBranch && <Badge text="Branch" title="An operating hire branch" />}
                  {flags && c.InGLMinor && <Badge text="M" title="Baseplan minor code" />}
                  {flags && c.InDebtorsDivision && <Badge text="D" title="Debtors division" />}
                </span>
              </td>
              <td className="px-3 py-1.5 font-mono text-xs">{c.SortOrder ?? ''}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{c.Locations}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{c.HomeBranchPeople}</td>
              <td className="px-3 py-1.5 text-xs">
                {c.IsActive ? 'Active' : 'Inactive'}
                {c.ExcludeFromDailySales && <span className="ml-1 text-darkGrey/60">· not in daily sales</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={10} className="px-3 py-4 text-center text-sm text-darkGrey/60">No reporting branches match.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Badge({ text, title }: { text: string; title?: string }) {
  return <span title={title} className="rounded bg-[#E6ECF2] px-1 text-[10px] font-semibold text-navy">{text}</span>;
}
